import type { CSSProperties } from 'react';
import type { HeroBlockProps } from '../types';
import { theme, sectionColors, cssColor } from '../theme';
import { HeroVideo } from './HeroVideo';
import { heroImage } from '../responsiveImage';
import { imageFocusPosition } from '../imageFocus';

/** `fetchpriority` (lowercase): React 18 has no `fetchPriority` prop and would warn at hydration. */
const HIGH_PRIORITY = { fetchpriority: 'high' } as Record<string, string>;

/**
 * Pure presentational hero. Props-only, no hooks/handlers, no "use client".
 *
 * Layout variants (Pick-a-Look): 'classic' (default) is the full-bleed image/
 * video/carousel hero with overlaid text — UNCHANGED, so every existing site
 * renders exactly as before. 'split' / 'minimal' / 'editorial' are contained,
 * surface-themed layouts used by template looks; they use a single image (no
 * video/carousel) and no dark scrim. All server-safe.
 */
export function HeroBlock(props: HeroBlockProps & { previewStatic?: boolean }) {
  const credit = props.photoCredit;
  if (credit?.name?.trim()) {
    return (
      <div className="relative">
        <HeroVariant {...props} />
        <PhotoCredit {...credit} />
      </div>
    );
  }
  return <HeroVariant {...props} />;
}

/** "Photo: Jane Doe / Unsplash" — small, in the corner, on top of whichever layout is showing. */
function PhotoCredit({ name, url, source }: NonNullable<HeroBlockProps['photoCredit']>) {
  const label = `Photo: ${name.trim()}${source ? ` / ${source.charAt(0).toUpperCase()}${source.slice(1)}` : ''}`;
  const safeUrl = url && /^https:\/\//.test(url) ? url : null;
  return (
    <p className="pointer-events-auto absolute bottom-1 right-2 z-20 m-0 text-[10px] leading-tight text-white/80 drop-shadow">
      {safeUrl ? (
        <a href={safeUrl} target="_blank" rel="noopener noreferrer nofollow" className="text-inherit underline-offset-2 hover:underline">
          {label}
        </a>
      ) : (
        label
      )}
    </p>
  );
}

function HeroVariant(props: HeroBlockProps & { previewStatic?: boolean }) {
  switch (props.variant) {
    case 'split':
      return <HeroSplit {...props} />;
    case 'minimal':
      return <HeroMinimal {...props} />;
    case 'editorial':
      return <HeroEditorial {...props} />;
    case 'band':
      return <HeroBand {...props} />;
    default:
      return <HeroClassic {...props} />;
  }
}

// ── Classic (default) — full-bleed media + overlaid text. UNCHANGED. ────────────
function HeroClassic(props: HeroBlockProps & { previewStatic?: boolean }) {
  const {
    heading,
    subheading,
    backgroundImageUrl,
    ctaLabel,
    ctaHref,
    alignment = 'center',
    showSearch = false,
    searchPlaceholder,
    mediaType = 'image',
    carouselImages,
    videoUrl,
    videoPosterUrl,
    previewStatic,
  } = props;
  const align =
    alignment === 'left' ? 'items-start text-left'
      : alignment === 'right' ? 'items-end text-right'
        : 'items-center text-center';
  // Resolve the media. A carousel needs ≥2 images (else it's just an image); a
  // video needs a URL. Anything else falls back to the single background image.
  const slides = (carouselImages ?? []).map((s) => (s ?? '').trim()).filter(Boolean);
  const useVideo = mediaType === 'video' && !!(videoUrl ?? '').trim();
  const useCarousel = mediaType === 'carousel' && slides.length > 1;
  // Collage: a full-bleed mosaic of the provided images (cycled to fill 6 cells). Needs ≥1 image.
  const useCollage = mediaType === 'collage' && slides.length > 0;
  const collageCells = useCollage ? Array.from({ length: 6 }, (_, i) => slides[i % slides.length]) : [];
  const useMediaLayer = useVideo || useCarousel || useCollage;
  // A PHOTO hero takes `text_on_dark`, not the colour that reads on the secondary.
  //
  // The words sit on the image, over a scrim — the secondary is not behind them and has no say
  // in what reads. Deriving from it meant a brand with a light secondary put dark ink over a
  // dark photograph: contrast computed perfectly, for the wrong background. Photoless heroes
  // still take onSecondary, because there the band really is the secondary.
  const overMedia = useMediaLayer || !!(backgroundImageUrl ?? '').trim();
  const base = sectionColors(props, { bg: theme.secondary, text: theme.onSecondary });
  /**
   * The text colour, decided AFTER `sectionColors` rather than handed to it as a default.
   *
   * It has to be after, because `sectionColors` consults a `backgroundRole`'s on-colour BEFORE it
   * ever reaches the default — so passing the right answer as a default did nothing for any hero
   * that also named a role, and a light role over a dark photograph still produced dark ink on the
   * picture. Over media the background role paints nothing visible, so it has no business choosing
   * the text either.
   *
   * Three cases, in order:
   *   1. the tenant named a colour or a text role — their word, untouched;
   *   2. media this component can SEE (an image prop, carousel, video, collage) — `text_on_dark`;
   *   3. everything else — `var(--hero-text, …)`. A hero can be over a photo the component cannot
   *      see: with no media props the section falls back to `background-image: var(--hero-image)`,
   *      set by the renderers from the site's own hero image. They emit `--hero-text` beside it,
   *      present only when there IS an image, so the var wins on a photo and collapses to the
   *      role-or-secondary colour on a photoless band.
   */
  const colors =
    props.textColor?.trim() || props.textRole
      ? base
      : overMedia
        ? { ...base, color: theme.onDark }
        : { ...base, color: `var(--hero-text, ${base.color})` };

  // Image mode: the photo is a real <img> (object-cover, so it looks exactly like the old
  // background-image) with a srcset when the gateway serves it — a phone downloads the 1080
  // copy, not the 2560 one. With no URL the section keeps the `--hero-image` CSS fallback.
  // Media-layer modes paint behind the content and add a dark scrim so the text stays legible.
  const bgImage = !useMediaLayer && backgroundImageUrl ? heroImage(backgroundImageUrl) : null;
  // The focus point: which part of the photo stays in view when the hero crops it.
  const focusStyle = objectFocusStyle(props.imageFocus);
  const bgFocusStyle = backgroundFocusStyle(props.imageFocus);
  const sectionStyle = {
    ...colors,
    // Pick-a-Look → Hero Section height. `--hero-min-height` is set site-wide from
    // config.section_looks.heroHeight (apps/tenant-sites layout.tsx); the fallback (60vh) is
    // this block's own long-standing default, so an unconfigured site is unaffected.
    minHeight: 'var(--hero-min-height, 60vh)',
    ...(useMediaLayer || bgImage ? {} : { backgroundImage: 'var(--hero-image, none)' }),
  };

  return (
    <section
      className={`relative flex flex-col justify-center overflow-hidden bg-cover bg-center px-6 py-24 ${align}`}
      style={sectionStyle}
    >
      {bgImage ? (
        <img
          src={bgImage.src}
          srcSet={bgImage.srcSet}
          sizes={bgImage.sizes}
          {...photoA11y(props.imageAlt)}
          {...HIGH_PRIORITY}
          decoding="async"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={focusStyle}
        />
      ) : null}
      {useVideo ? (
        previewStatic ? (
          // Builder preview: render a STATIC frame (poster / bg image) instead of an
          // autoplaying <video>, so a large/4K hero video can't freeze the editor.
          <>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: (videoPosterUrl || backgroundImageUrl)
                  ? `url("${videoPosterUrl || backgroundImageUrl}")`
                  : undefined,
                ...bgFocusStyle,
              }}
              aria-hidden="true"
            />
            <Scrim opacity={props.overlayOpacity} fallback={40} />
            <div className="absolute right-3 top-3 z-20 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-medium text-white">
              ▶ Video background — plays on the live site
            </div>
          </>
        ) : (
          // Live site: the poster paints first (server-rendered, so it is the LCP image and
          // can be preloaded); the video mounts over it client-side, desktop only, after
          // load — see HeroVideo. The <video> used to be in the HTML with autoplay, which
          // made phones download the whole file before anything else.
          <>
            {(videoPosterUrl || backgroundImageUrl) ? (
              <img
                {...heroImage(videoPosterUrl || backgroundImageUrl || '')}
                alt=""
                {...HIGH_PRIORITY}
                decoding="async"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                style={focusStyle}
                aria-hidden="true"
              />
            ) : null}
            <HeroVideo src={(videoUrl ?? '').trim()} poster={videoPosterUrl || undefined} />
            <Scrim opacity={props.overlayOpacity} fallback={40} />
          </>
        )
      ) : useCarousel ? (
        <>
          <div className={`blk-hero-carousel-${slides.length} absolute inset-0`} aria-hidden="true">
            {slides.map((src, i) => (
              <div
                key={`${i}-${src}`}
                className="blk-hero-slide absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url("${src}")`, ...bgFocusStyle }}
              />
            ))}
          </div>
          <Scrim opacity={props.overlayOpacity} fallback={30} />
          <style dangerouslySetInnerHTML={{ __html: carouselCss(slides.length) }} />
        </>
      ) : useCollage ? (
        <>
          {/* Full-bleed image mosaic (server-safe, no JS). 3×2 on desktop, 2×3 on mobile. */}
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-3 gap-0.5 md:grid-cols-3 md:grid-rows-2" aria-hidden="true">
            {collageCells.map((src, i) => (
              <div key={`${i}-${src}`} className="bg-cover bg-center" style={{ backgroundImage: `url("${src}")`, ...bgFocusStyle }} />
            ))}
          </div>
          <Scrim opacity={props.overlayOpacity} fallback={45} />
        </>
      ) : null}

      {/* Legibility scrim for the overlaid white text. The media-layer modes (video/
          carousel) add their own above; this covers the plain background-image case
          (a bright photo) and adds a gentle lift over solid-color heroes so white text
          stays readable on mid-tone bands (e.g. a teal section), not just dark ones. */}
      {!useMediaLayer ? (
        <Scrim opacity={props.overlayOpacity} fallback={backgroundImageUrl ? 40 : 20} className="pointer-events-none z-0" />
      ) : null}

      <div className={`relative z-10 flex w-full flex-col ${align}`}>
        <h1 className="max-w-3xl text-4xl font-semibold drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] md:text-6xl" style={{ fontFamily: theme.headingFont }}>{heading}</h1>
        {subheading ? <p className="mt-4 max-w-2xl text-lg opacity-90 drop-shadow md:text-xl">{subheading}</p> : null}
        {showSearch ? (
          // Server-safe GET form → the tenant's /listings results page. name="address"
          // → the gateway matches with contains(UnparsedAddress), which spans street +
          // city + state + zip, so the one box searches all of them. Empty submit
          // shows all listings.
          <HeroSearchForm {...searchStyleOf(props)} searchPlaceholder={searchPlaceholder} className="mt-8 w-full max-w-xl" />
        ) : null}
        {ctaLabel && ctaHref ? (
          <a
            href={ctaHref}
            className="blk-shaped mt-8 inline-block rounded-md px-6 py-3 font-medium hover:opacity-90"
            style={{ backgroundColor: cssColor(props.ctaBgColor) ?? theme.accent, color: cssColor(props.ctaTextColor) ?? theme.secondary }}
          >
            {ctaLabel}
          </a>
        ) : null}
      </div>
    </section>
  );
}

// ── Split — contained two-column: text/search beside a tall image. ──────────────
function HeroSplit(props: HeroBlockProps) {
  const { heading, subheading, backgroundImageUrl, ctaLabel, ctaHref, showSearch, searchPlaceholder } = props;
  const colors = sectionColors(props, { bg: theme.background, text: theme.text });
  return (
    <section style={colors}>
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-stretch lg:grid-cols-2">
        <div className="flex flex-col justify-center px-6 py-16 md:px-12 md:py-24">
          <h1 className="text-4xl font-semibold leading-tight md:text-5xl" style={{ fontFamily: theme.headingFont }}>{heading}</h1>
          {subheading ? <p className="mt-4 max-w-xl text-lg opacity-80">{subheading}</p> : null}
          {showSearch ? <HeroSearchForm {...searchStyleOf(props)} searchPlaceholder={searchPlaceholder} className="mt-8 max-w-md" /> : null}
          {ctaLabel && ctaHref ? <HeroCta {...ctaStyleOf(props)} ctaLabel={ctaLabel} ctaHref={ctaHref} className="mt-8 self-start" /> : null}
        </div>
        <div
          className="relative min-h-[42vh] bg-cover bg-center lg:min-h-[36rem]"
          style={{ backgroundImage: backgroundImageUrl ? `url("${backgroundImageUrl}")` : 'var(--hero-image, none)', ...backgroundFocusStyle(props.imageFocus) }}
          {...photoBoxA11y(backgroundImageUrl ? props.imageAlt : undefined)}
        />
      </div>
    </section>
  );
}

// ── Minimal — contained, centered, big headline + generous whitespace. ──────────
function HeroMinimal(props: HeroBlockProps) {
  const { heading, subheading, backgroundImageUrl, ctaLabel, ctaHref, showSearch, searchPlaceholder } = props;
  const colors = sectionColors(props, { bg: theme.background, text: theme.text });
  return (
    <section style={colors} className="px-6 py-24 md:py-32">
      <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
        <h1 className="max-w-3xl text-5xl font-semibold leading-[1.05] md:text-7xl" style={{ fontFamily: theme.headingFont }}>{heading}</h1>
        {subheading ? <p className="mt-6 max-w-2xl text-lg opacity-80 md:text-xl">{subheading}</p> : null}
        {showSearch ? <HeroSearchForm {...searchStyleOf(props)} searchPlaceholder={searchPlaceholder} className="mt-10 w-full max-w-xl" /> : null}
        {ctaLabel && ctaHref ? <HeroCta {...ctaStyleOf(props)} ctaLabel={ctaLabel} ctaHref={ctaHref} className="mt-10" /> : null}
      </div>
      {backgroundImageUrl ? (
        <div
          className="mx-auto mt-16 aspect-[16/7] w-full max-w-6xl bg-cover bg-center"
          style={{ backgroundImage: `url("${backgroundImageUrl}")`, ...backgroundFocusStyle(props.imageFocus) }}
          {...photoBoxA11y(props.imageAlt)}
        />
      ) : null}
    </section>
  );
}

// ── Band — a compact solid band, centred heading + subheading (an inner page's header). ──
// What the built-in About and Contact pages always opened with; seeded onto those pages so they
// look the same once they are block pages. No image, no scrim: the band is the colour.
// `alignment` left/right moves the text (the /sold and /coming-soon headers are left-aligned);
// blank or 'center' is the centred band.
function HeroBand(props: HeroBlockProps) {
  const { heading, subheading, ctaLabel, ctaHref, alignment } = props;
  const colors = sectionColors(props, { bg: theme.primary, text: theme.onPrimary });
  const align = alignment === 'left' ? 'text-left' : alignment === 'right' ? 'text-right' : 'text-center';
  const sub = alignment === 'left' ? 'max-w-2xl' : alignment === 'right' ? 'ml-auto max-w-2xl' : 'mx-auto max-w-2xl';
  return (
    <section style={colors} className="px-6 py-20">
      <div className={`mx-auto max-w-7xl ${align}`}>
        <h1 className="mb-4 text-4xl font-bold md:text-5xl" style={{ fontFamily: theme.headingFont }}>{heading}</h1>
        {subheading ? <p className={`${sub} text-lg opacity-80`}>{subheading}</p> : null}
        {ctaLabel && ctaHref ? <HeroCta {...ctaStyleOf(props)} ctaLabel={ctaLabel} ctaHref={ctaHref} className="mt-8 inline-block" /> : null}
      </div>
    </section>
  );
}

// ── Editorial — asymmetric: oversized headline beside an offset portrait image. ──
function HeroEditorial(props: HeroBlockProps) {
  const { heading, subheading, backgroundImageUrl, ctaLabel, ctaHref, showSearch, searchPlaceholder } = props;
  const colors = sectionColors(props, { bg: theme.background, text: theme.text });
  return (
    <section style={colors} className="px-6 py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 md:grid-cols-12 md:items-end">
        <div className="md:col-span-7">
          <h1 className="text-5xl font-semibold leading-[0.95] md:text-8xl" style={{ fontFamily: theme.headingFont }}>{heading}</h1>
          {subheading ? <p className="mt-6 max-w-lg text-lg opacity-80">{subheading}</p> : null}
          {(showSearch || (ctaLabel && ctaHref)) ? (
            <div className="mt-8 flex flex-wrap items-center gap-4">
              {showSearch ? <HeroSearchForm {...searchStyleOf(props)} searchPlaceholder={searchPlaceholder} className="w-full max-w-md" /> : null}
              {ctaLabel && ctaHref ? <HeroCta {...ctaStyleOf(props)} ctaLabel={ctaLabel} ctaHref={ctaHref} /> : null}
            </div>
          ) : null}
        </div>
        <div className="md:col-span-5">
          {backgroundImageUrl ? (
            <div
              className="aspect-[3/4] w-full bg-cover bg-center"
              style={{ backgroundImage: `url("${backgroundImageUrl}")`, ...backgroundFocusStyle(props.imageFocus) }}
              {...photoBoxA11y(props.imageAlt)}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ── The photo's description and focus point ─────────────────────────────────────
/** An <img>'s alt: the owner's description when there is one, else decoration hidden from screen readers. */
function photoA11y(alt: string | undefined): { alt: string; 'aria-hidden'?: true } {
  const text = (alt ?? '').trim();
  return text ? { alt: text } : { alt: '', 'aria-hidden': true };
}

/** The same for a photo painted as a background: a described one is an image, a blank one is hidden. */
function photoBoxA11y(alt: string | undefined): { role?: 'img'; 'aria-label'?: string; 'aria-hidden'?: true } {
  const text = (alt ?? '').trim();
  return text ? { role: 'img', 'aria-label': text } : { 'aria-hidden': true };
}

function objectFocusStyle(focus: unknown): CSSProperties | undefined {
  const position = imageFocusPosition(focus);
  return position ? { objectPosition: position } : undefined;
}

function backgroundFocusStyle(focus: unknown): CSSProperties {
  const position = imageFocusPosition(focus);
  return position ? { backgroundPosition: position } : {};
}

// ── Shared bits for the contained variants ──────────────────────────────────────
/**
 * The search bar's own colours. Each is optional and falls back to the theme, so a hero that sets
 * none of them renders exactly as before. They exist because the bar used to be painted ONLY by
 * the site-wide `surface` token: "make the search bar white" had no per-hero answer, and the chat
 * could only fail or recolour every card on the site.
 */
interface HeroSearchStyle {
  searchBgColor?: string;
  searchTextColor?: string;
  searchButtonColor?: string;
  searchButtonTextColor?: string;
}

function searchStyleOf(props: HeroBlockProps): HeroSearchStyle {
  return {
    searchBgColor: props.searchBgColor,
    searchTextColor: props.searchTextColor,
    searchButtonColor: props.searchButtonColor,
    searchButtonTextColor: props.searchButtonTextColor,
  };
}

function HeroSearchForm({
  searchPlaceholder,
  className = '',
  searchBgColor,
  searchTextColor,
  searchButtonColor,
  searchButtonTextColor,
}: HeroSearchStyle & { searchPlaceholder?: string; className?: string }) {
  const buttonBg = cssColor(searchButtonColor);
  const buttonFg = cssColor(searchButtonTextColor);
  // The button's colours travel as CUSTOM PROPERTIES that feed `.blk-btn`, never as an inline
  // background — an inline colour would beat the stylesheet and make the hover role inert
  // (see blocks.css). A pinned button colour also pins its hover, via --blk-btn-bg.
  const buttonVars = {
    ...(buttonBg ? { '--blk-btn-bg': buttonBg } : {}),
    ...(buttonFg ? { '--blk-btn-fg': buttonFg } : {}),
  } as CSSProperties;
  return (
    <form
      action="/listings"
      method="get"
      className={`flex overflow-hidden rounded-md shadow-lg ${className}`}
      style={{ backgroundColor: cssColor(searchBgColor) ?? theme.surface }}
    >
      <input
        type="text"
        name="address"
        placeholder={searchHintOf(searchPlaceholder)}
        className="w-full px-4 py-3 outline-none bg-transparent"
        style={{ color: cssColor(searchTextColor) ?? theme.text }}
      />
      <button type="submit" className="blk-btn shrink-0 px-6 py-3 font-medium" style={buttonVars}>
        Search
      </button>
    </form>
  );
}

function ctaStyleOf(p: HeroBlockProps): { ctaBgColor?: string; ctaTextColor?: string } {
  return { ctaBgColor: p.ctaBgColor, ctaTextColor: p.ctaTextColor };
}

function HeroCta({
  ctaLabel,
  ctaHref,
  ctaBgColor,
  ctaTextColor,
  className = '',
}: { ctaLabel: string; ctaHref: string; ctaBgColor?: string; ctaTextColor?: string; className?: string }) {
  // A pinned colour goes through --blk-btn-bg/-fg, never an inline background: an inline
  // declaration would beat .blk-btn:hover (see blocks.css), and the pin must hold on hover too.
  const bg = cssColor(ctaBgColor);
  const fg = cssColor(ctaTextColor);
  const style = {
    ...(bg ? { '--blk-btn-bg': bg } : {}),
    ...(fg ? { '--blk-btn-fg': fg } : {}),
  } as CSSProperties;
  return (
    <a
      href={ctaHref}
      className={`blk-btn inline-block rounded-md px-6 py-3 font-medium hover:opacity-90 ${className}`}
      style={style}
    >
      {ctaLabel}
    </a>
  );
}

function searchHintOf(searchPlaceholder?: string): string {
  // Fall back on a sensible placeholder for undefined AND blank '' (the form's
  // default is '', and the AI may omit it) — otherwise the bar shows no hint.
  return searchPlaceholder?.trim() ? searchPlaceholder : 'City, neighborhood, or ZIP…';
}

/**
 * CSS-only cross-fade carousel keyframes for N slides (server-safe, no JS). Each
 * slide is featured ~5s; the N slides cycle in order with a soft cross-fade at the
 * seams. Stacked slides default to opacity:0; the staggered animation reveals one
 * at a time.
 */
function carouselCss(n: number): string {
  const perSlide = 5; // seconds each slide is featured
  const total = n * perSlide;
  const slicePct = 100 / n;
  const fadePct = Math.min(6, slicePct / 3);
  const a = fadePct.toFixed(3);
  const b = Math.max(fadePct, slicePct - fadePct).toFixed(3);
  const c = (slicePct + fadePct).toFixed(3);
  const name = `blkHeroFade${n}`;
  let css =
    `@keyframes ${name}{0%{opacity:0}${a}%{opacity:1}${b}%{opacity:1}${c}%{opacity:0}100%{opacity:0}}` +
    `.blk-hero-carousel-${n} .blk-hero-slide{opacity:0;animation:${name} ${total}s infinite;animation-timing-function:ease-in-out}`;
  for (let i = 0; i < n; i++) {
    css += `.blk-hero-carousel-${n} .blk-hero-slide:nth-child(${i + 1}){animation-delay:${i * perSlide}s}`;
  }
  return css;
}

/**
 * The darkening layer over a classic hero's media (plan E8). `opacity` is the tenant's 0–90 (%);
 * blank keeps the layer's built-in default, so an existing hero renders exactly as before. Capped
 * at 90 — a fully black layer hides the photo it exists to make readable.
 */
function Scrim({ opacity, fallback, className = '' }: { opacity?: number; fallback: number; className?: string }) {
  const pct = typeof opacity === 'number' && Number.isFinite(opacity) ? Math.min(90, Math.max(0, opacity)) : fallback;
  return (
    <div
      className={`absolute inset-0 ${className}`}
      style={{ backgroundColor: `rgba(0, 0, 0, ${pct / 100})` }}
      aria-hidden="true"
    />
  );
}
